/**
 * ImageModelPicker — Higgsfield-style model selector.
 * Renders a compact trigger button with the currently selected model and opens
 * a popover with searchable, grouped list of featured/all models.
 */
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Check, ChevronDown, Search, Sparkles, Grid3x3 } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";

export interface ImageModelOption {
  id: string;
  label: string;
  provider: string;
  description: string;
  badge?: "NEW" | "PREMIUM" | "BETA";
  featured?: boolean;
  /** Short letter shown inside the colored avatar */
  glyph?: string;
  /** Tailwind class for avatar background (e.g. "bg-lime-400 text-black") */
  accent?: string;
}

interface Props {
  models: ImageModelOption[];
  value: string;
  onChange: (id: string) => void;
  disabled?: boolean;
}

const BADGE_STYLES: Record<NonNullable<ImageModelOption["badge"]>, string> = {
  NEW: "bg-lime-400/20 text-lime-300 border-lime-400/30",
  PREMIUM: "bg-lime-400 text-black border-transparent",
  BETA: "bg-amber-400/20 text-amber-300 border-amber-400/30",
};

function ModelAvatar({ model, size = "md" }: { model: ImageModelOption; size?: "sm" | "md" }) {
  const dim = size === "sm" ? "w-7 h-7 text-[11px]" : "w-9 h-9 text-xs";
  return (
    <div
      className={cn(
        "flex items-center justify-center rounded-lg font-bold shrink-0",
        dim,
        model.accent ?? "bg-white/[0.06] text-white/80"
      )}
    >
      {model.glyph ?? model.label[0]}
    </div>
  );
}

function ModelRow({
  model,
  selected,
  onSelect,
}: {
  model: ImageModelOption;
  selected: boolean;
  onSelect: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onSelect}
      className={cn(
        "w-full flex items-start gap-3 px-2.5 py-2 rounded-lg text-left transition-colors",
        selected ? "bg-white/[0.06]" : "hover:bg-white/[0.04]"
      )}
    >
      <ModelAvatar model={model} />
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-1.5 flex-wrap">
          <span className="text-[13px] font-medium text-white leading-tight">{model.label}</span>
          {model.badge && (
            <span
              className={cn(
                "text-[9px] font-bold px-1.5 py-0.5 rounded border leading-none tracking-wide",
                BADGE_STYLES[model.badge]
              )}
            >
              {model.badge}
            </span>
          )}
        </div>
        <p className="text-[11px] text-white/45 mt-0.5 leading-snug line-clamp-2">
          {model.description}
        </p>
      </div>
      {selected && <Check className="w-4 h-4 text-lime-400 shrink-0 mt-1.5" />}
    </button>
  );
}

export function ImageModelPicker({ models, value, onChange, disabled }: Props) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");

  const selected = models.find((m) => m.id === value) ?? models[0];

  const { featured, all } = useMemo(() => {
    const q = query.trim().toLowerCase();
    const filter = (m: ImageModelOption) =>
      !q ||
      m.label.toLowerCase().includes(q) ||
      m.description.toLowerCase().includes(q) ||
      m.provider.toLowerCase().includes(q);
    return {
      featured: models.filter((m) => m.featured && filter(m)),
      all: models.filter((m) => !m.featured && filter(m)),
    };
  }, [models, query]);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          disabled={disabled}
          className={cn(
            "inline-flex items-center gap-2 pl-1.5 pr-3 py-1.5 rounded-full border transition-colors",
            "bg-white/[0.04] border-white/[0.1] hover:border-white/[0.2]",
            "disabled:opacity-50 disabled:cursor-not-allowed"
          )}
        >
          <ModelAvatar model={selected} size="sm" />
          <span className="text-[13px] font-medium text-white">{selected.label}</span>
          <ChevronDown className="w-3.5 h-3.5 text-white/50" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        sideOffset={10}
        className="w-[340px] p-0 bg-[#0b0d12] border border-white/[0.08] rounded-2xl shadow-2xl shadow-black/60 text-white"
      >
        <div className="p-2.5 border-b border-white/[0.06]">
          <div className="relative">
            <Search className="w-3.5 h-3.5 text-white/40 absolute left-2.5 top-1/2 -translate-y-1/2" />
            <Input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={t("imageModelPicker.searchPlaceholder")}
              className="h-8 pl-8 bg-white/[0.03] border-white/[0.06] text-[13px] text-white placeholder:text-white/35 focus-visible:ring-0 focus-visible:border-white/[0.15]"
            />
          </div>
        </div>

        <div className="max-h-[420px] overflow-y-auto p-2 space-y-3">
          {featured.length > 0 && (
            <div>
              <div className="flex items-center gap-1.5 px-2 py-1 text-[10px] font-semibold uppercase tracking-wider text-white/45">
                <Sparkles className="w-3 h-3" /> {t("imageModelPicker.featured")}
              </div>
              <div className="space-y-0.5">
                {featured.map((m) => (
                  <ModelRow
                    key={m.id}
                    model={m}
                    selected={m.id === value}
                    onSelect={() => {
                      onChange(m.id);
                      setOpen(false);
                    }}
                  />
                ))}
              </div>
            </div>
          )}

          {all.length > 0 && (
            <div>
              <div className="flex items-center gap-1.5 px-2 py-1 text-[10px] font-semibold uppercase tracking-wider text-white/45">
                <Grid3x3 className="w-3 h-3" /> {t("imageModelPicker.all")}
              </div>
              <div className="space-y-0.5">
                {all.map((m) => (
                  <ModelRow
                    key={m.id}
                    model={m}
                    selected={m.id === value}
                    onSelect={() => {
                      onChange(m.id);
                      setOpen(false);
                    }}
                  />
                ))}
              </div>
            </div>
          )}

          {featured.length === 0 && all.length === 0 && (
            <div className="px-3 py-8 text-center text-[12px] text-white/40">
              {t("imageModelPicker.noResults")}
            </div>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
